import { useCallback, useEffect, useMemo, useState } from "react";
import useBillingApi from "../api/use-billing-api";
import type { BillingPlanId } from "../types/billing";
import { initializePaddle, previewPaddlePrices } from "../utils/paddle-client";

export type LocalizedPlanPrices = Record<BillingPlanId, string>;

const PENDING_PRICES: LocalizedPlanPrices = {
  pro_monthly: "...",
  agency_monthly: "...",
};

const FALLBACK_PRICES: LocalizedPlanPrices = {
  pro_monthly: "See checkout",
  agency_monthly: "See checkout",
};

const PLAN_IDS: BillingPlanId[] = ["pro_monthly", "agency_monthly"];

const getPreviewPriceText = (lineItem: {
  formattedTotals?: { subtotal?: string; total?: string };
  formattedUnitTotals?: { subtotal?: string; total?: string };
}) =>
  lineItem.formattedTotals?.total ||
  lineItem.formattedUnitTotals?.total ||
  lineItem.formattedTotals?.subtotal ||
  lineItem.formattedUnitTotals?.subtotal ||
  "";

const getMonthlyLabel = (price: string) =>
  price.startsWith("$") || price.startsWith("₹") ? `${price}/month` : price;

const useLocalizedPricing = () => {
  const { getCheckoutConfig } = useBillingApi();
  const [prices, setPrices] = useState<LocalizedPlanPrices>(PENDING_PRICES);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const config = await getCheckoutConfig();
      if (!config?.clientToken) {
        setPrices(FALLBACK_PRICES);
        return;
      }

      const previewItems = PLAN_IDS.flatMap((planId) => {
        const priceId = config.priceIds[planId];
        return priceId ? [{ planId, priceId, quantity: 1 }] : [];
      });

      if (!previewItems.length) {
        setPrices(FALLBACK_PRICES);
        return;
      }

      await initializePaddle(config.clientToken, config.environment);
      const response = await previewPaddlePrices(
        previewItems.map(({ priceId, quantity }) => ({ priceId, quantity }))
      );
      const lineItems = response.data?.details?.lineItems ?? [];
      const nextPrices = { ...FALLBACK_PRICES };

      previewItems.forEach(({ planId, priceId }) => {
        const lineItem = lineItems.find((item) => item.price?.id === priceId);
        const previewPrice = lineItem ? getPreviewPriceText(lineItem) : "";
        if (previewPrice) {
          nextPrices[planId] = previewPrice;
        }
      });

      setPrices(nextPrices);
    } catch (error) {
      console.error("Failed to load localized Paddle prices", error);
      setPrices(FALLBACK_PRICES);
    } finally {
      setLoading(false);
    }
  }, [getCheckoutConfig]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return useMemo(
    () => ({
      prices,
      loading,
      refresh,
      proMonthly: prices.pro_monthly,
      agencyMonthly: prices.agency_monthly,
      proMonthlyLabel: getMonthlyLabel(prices.pro_monthly),
      agencyMonthlyLabel: getMonthlyLabel(prices.agency_monthly),
      upgradeFromLabel:
        prices.pro_monthly.startsWith("$") || prices.pro_monthly.startsWith("₹")
          ? `From ${prices.pro_monthly}/month`
          : prices.pro_monthly,
    }),
    [loading, prices, refresh]
  );
};

export default useLocalizedPricing;
